import { Description, Event } from "@mui/icons-material";
import { Button, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { GqlPermissions } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { dateForUrlFormatter } from "~/modules/app/util/formatters";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import WorkerReportStatusChip from "~/modules/fieldworker/components/ProjectVisitWorkerReportStatusChip/WorkerReportStatusChip";

import { ShiftVisitsFragment } from "./fragments.generated";

type Props = {
    shift: ShiftVisitsFragment;
};

export default function ShiftVisits({ shift }: Props) {
    const { t } = useTranslation("fieldworker");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    if (!shift.summary || !shift.plannerVisits?.length) {
        return null;
    }

    return (
        <Stack gap={2} divider={<Divider />}>
            {shift.plannerVisits.map(visit => {
                const linkToPlanner = `/planner/${dateForUrlFormatter(parseStringToDate(shift.startTime))}/areas/${visit.plannerAreaId}/visits/${visit.id}`;
                const linkToSummary = `/c/${visit.plannerArea?.project?.client?.slug}/p/${visit.plannerArea?.project?.slug}/visits/${visit.id}`;

                return (
                    <Stack key={visit.id} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} width="100%">
                            <Typography variant="caption" color="textSecondary">
                                {visit.protocol?.name}
                            </Typography>
                            <WorkerReportStatusChip
                                status={visit.reportsV2?.find(r => r.plannerShiftId === shift.id)?.status ?? "Planned"}
                            />
                        </Stack>

                        <Stack direction="row" alignItems="center">
                            {isAdmin && (
                                <>
                                    <Tooltip title={t("shiftVisits.btnOpenPlanner")}>
                                        <Button component={RouterLink} to={linkToPlanner} color="inherit">
                                            <Event />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={t("shiftVisits.btnOpenPlannedVisit")}>
                                        <Button component={RouterLink} to={linkToSummary} color="inherit">
                                            <Description />
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        </Stack>
                    </Stack>
                );
            })}
        </Stack>
    );
}
